import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import TimeReportEntriesDrawer from './TimeReportEntriesDrawer';
import TimeReportFilters from './TimeReportFilters';
import { GROUPING_ROLE, PERIOD_ALL_TIME, PERIOD_CUSTOM, TYPE_ALL } from './timeReportFormat';
import TimeReportKpis from './TimeReportKpis';
import TimeReportTable from './TimeReportTable';

import Alert from 'CommonComponents/Alert';
import SelectInput from 'CommonComponents/SelectInput';

const EMPTY_ROWS = [];

/**
 * The filters as query parameters. The export link and both endpoints read the
 * same object, so a sheet can never reflect a different period from the table it
 * was downloaded from.
 */
function queryFor(eventId, filters) {
    const params = { event: eventId, grouping: filters.grouping, type: filters.type, period: filters.period };

    if (filters.period === PERIOD_CUSTOM) {
        if (filters.from) {
            params.from = filters.from;
        }

        if (filters.to) {
            params.to = filters.to;
        }
    }

    return params;
}

/**
 * The project the page opens on. The server resolved any `event` parameter
 * against this viewer's own project list before it reached here, so an id that
 * arrives is one they may read and an unresolvable one — another
 * organisation's project, or one they no longer administer — arrives empty and
 * falls back to their first project rather than opening nothing.
 */
function initialEventId(deepLinkedEventId, events) {
    if (deepLinkedEventId) {
        return String(deepLinkedEventId);
    }

    return events.length > 0 ? String(events[0].id) : '';
}

export default function ProjectTimeReportIndex({ dataUrl, entriesUrl, exportUrl, organisationName, events, currency, deepLinkedEventId }) {
    const [selectedEventId, setSelectedEventId] = useState(() => initialEventId(deepLinkedEventId, events));
    const [filters, setFilters] = useState({
        grouping: GROUPING_ROLE,
        type: TYPE_ALL,
        period: PERIOD_ALL_TIME,
        from: '',
        to: '',
    });

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(events.length > 0);
    const [error, setError] = useState(null);

    // The row whose panel is open, plus its entries. Held separately from the
    // report so re-reading the table does not close the panel.
    const [drillRow, setDrillRow] = useState(null);
    const [entries, setEntries] = useState([]);
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [entriesError, setEntriesError] = useState(null);

    const readSeq = useRef(0);
    const entriesSeq = useRef(0);

    const fetchReport = useCallback(() => {
        if (!selectedEventId) {
            return undefined;
        }

        const sequence = ++readSeq.current;

        setLoading(true);
        setError(null);

        return axios
            .get(dataUrl, { params: queryFor(selectedEventId, filters) })
            .then(({ data }) => {
                // Only the newest read may land. The filters stay live while one
                // is in flight, so an earlier response arriving last would put a
                // table on screen that no longer matches the controls above it.
                if (sequence === readSeq.current) {
                    setReport(data.data);
                }
            })
            .catch(() => {
                if (sequence === readSeq.current) {
                    setError($t('Something went wrong'));
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [dataUrl, selectedEventId, filters]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const openDrillThrough = useCallback(
        (row) => {
            const sequence = ++entriesSeq.current;

            setDrillRow(row);
            setEntries([]);
            setEntriesError(null);
            setEntriesLoading(true);

            axios
                .get(entriesUrl, { params: { ...queryFor(selectedEventId, filters), group_key: row.group_key } })
                .then(({ data }) => {
                    // Only the newest read may land. Clicking a second row
                    // while the first is in flight would otherwise leave one
                    // row's entries under the other row's heading.
                    if (sequence === entriesSeq.current) {
                        setEntries(data.data.entries);
                    }
                })
                .catch(() => {
                    if (sequence === entriesSeq.current) {
                        setEntriesError($t('Something went wrong'));
                    }
                })
                .finally(() => {
                    if (sequence === entriesSeq.current) {
                        setEntriesLoading(false);
                    }
                });
        },
        [entriesUrl, selectedEventId, filters],
    );

    const changeFilters = useCallback((change) => {
        // The panel belongs to a row inside the filters it was opened under, so
        // changing them closes it rather than leaving entries beside a row that
        // may no longer exist.
        setDrillRow(null);
        setFilters((previous) => ({ ...previous, ...change }));
    }, []);

    const exportReport = useCallback(() => {
        const params = new URLSearchParams(queryFor(selectedEventId, filters));

        window.location.assign(`${exportUrl}?${params.toString()}`);
    }, [exportUrl, selectedEventId, filters]);

    // The picker is empty for whoever administers an organisation that holds no
    // projects. An Event Admin cannot reach this page without a project to
    // administer, so the message never has to cover them.
    if (events.length === 0) {
        return (
            <Alert type="info" role="status">
                {$t('This Organisation has no Projects to report time on.')}
            </Alert>
        );
    }

    const selectedEvent = events.find((event) => String(event.id) === String(selectedEventId));

    return (
        <div className="tw-space-y-4">
            {error && <Alert type="error">{error}</Alert>}

            <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
                <div>
                    {/* h2, not h1: inc/navbar.blade.php already yields the page
                        title as the page's h1. table__header carries the look. */}
                    <h2 className="table__header tw-mb-1">{$t('Project time report')}</h2>
                    <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                        {organisationName}
                        {selectedEvent ? ` × ${selectedEvent.name}` : ''}
                        {' · '}
                        <span className="tw-text-blue">{$t('Time')}</span>
                    </p>
                </div>

                <div>
                    <label htmlFor="time_report_event" className="tw-block tw-text-xs tw-text-charcoal-50 tw-mb-1">
                        {$t('Project')}
                    </label>
                    <SelectInput
                        id="time_report_event"
                        name="time_report_event"
                        value={selectedEventId}
                        onChange={(event) => {
                            setDrillRow(null);
                            setSelectedEventId(event.target.value);
                        }}
                    >
                        {events.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.name}
                            </option>
                        ))}
                    </SelectInput>
                </div>
            </div>

            {/* Permanent, not dismissible: what the figures are is part of
                reading them, and somebody arriving from a link has not seen it
                before whatever an earlier visitor dismissed. */}
            <Alert type="info" role="status">
                <strong>{$t('Memo ledger')}</strong>
                {' — '}
                {$t(
                    'time is recorded as a recharged cost. It never posts to actuals, commitments or cashflow — it exists to price time into the client budget and measure time margin.',
                )}
            </Alert>

            <TimeReportKpis kpis={report?.kpis} currency={currency} />

            <div className="table__card tw-space-y-4">
                {/* The controls stay live while a read is in flight — changing
                    your mind mid-request should not need the request to finish.
                    fetchReport()'s sequence guard is what keeps the table
                    matching them. */}
                <TimeReportFilters filters={filters} onChange={changeFilters} onExport={exportReport} />

                {loading && !report && <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>}

                {report && (
                    <TimeReportTable
                        groupLabel={report.columns.group}
                        rows={report.rows ?? EMPTY_ROWS}
                        total={report.total}
                        currency={currency}
                        onDrillThrough={openDrillThrough}
                    />
                )}
            </div>

            <TimeReportEntriesDrawer
                row={drillRow}
                entries={entries}
                loading={entriesLoading}
                error={entriesError}
                currency={currency}
                onClose={() => setDrillRow(null)}
            />
        </div>
    );
}

if (document.getElementById('ProjectTimeReport')) {
    const rootEl = document.getElementById('ProjectTimeReport');

    ReactDOM.render(
        <App>
            <ProjectTimeReportIndex
                dataUrl={rootEl.getAttribute('data-data-url')}
                entriesUrl={rootEl.getAttribute('data-entries-url')}
                exportUrl={rootEl.getAttribute('data-export-url')}
                organisationName={rootEl.getAttribute('data-organisation-name')}
                events={JSON.parse(rootEl.getAttribute('data-events') || '[]')}
                deepLinkedEventId={rootEl.getAttribute('data-selected-event')}
                currency={JSON.parse(rootEl.getAttribute('data-currency') || 'null')}
            />
        </App>,
        rootEl,
    );
}
